import { resolvePorchPlanSettings } from "building-generation/elements/porch/porchUtils";
import type { Porch } from "building-types/porches/porch";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByCenterWidthHeight } from "geom/rect2";

export function makeSheetGeometryForPorchFloors({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  if (!building.porches) {
    return [];
  }

  const result: SheetGeometry[] = [];

  for (const porch of Object.values(building.porches)) {
    if (porch.rooms[0].level.id !== levelId) {
      continue;
    }
    const linework: PolyLine2[] = [];

    linework.push(...porch.floorPerimeters);

    result.push(
      sheetPolylineGeometryNew({
        handle: building.levels[levelId].handle, // TODO -make more specific
        paths: linework,
      }),
    );
  }

  return result;
}

export function makeSheetGeometryForPorchColumns({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  if (!building.porches) {
    return [];
  }

  const result: SheetGeometry[] = [];

  for (const porch of Object.values(building.porches)) {
    if (porch.rooms[0].level.id !== levelId) {
      continue;
    }
    const linework: PolyLine2[] = [];
    const resolvedPorchPlanSettings = resolvePorchPlanSettings(building.settings?.planset, porch.porchSettings);
    makePorchColumnLinework(porch, linework, resolvedPorchPlanSettings.includePorchColumnStructure);

    result.push(
      sheetPolylineGeometryNew({
        handle: building.levels[levelId].handle, // TODO -make more specific
        paths: linework,
      }),
    );
  }

  return result;
}

export function makeSheetGeometryForPorchBeams({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  if (!building.porches) {
    return [];
  }

  const result: SheetGeometry[] = [];

  for (const porch of Object.values(building.porches)) {
    if (porch.rooms[0].level.id !== levelId) {
      continue;
    }
    const linework: PolyLine2[] = [];
    const resolvedPorchPlanSettings = resolvePorchPlanSettings(building.settings?.planset, porch.porchSettings);
    makePorchBeamLinework(porch, linework, resolvedPorchPlanSettings.includePorchHeaderStructure);

    result.push(
      sheetPolylineGeometryNew({
        handle: building.levels[levelId].handle, // TODO -make more specific
        paths: linework,
      }),
    );
  }

  return result;
}

export function makePorchBeamLinework(porch: Porch, linework: PolyLine2[], includeStructure: boolean = true) {
  const { internalHeaderStructureWidth, removeHeader, headerLateralOffset } = porch.porchSettings;

  if (!removeHeader) {
    for (const segment of porch.exteriorSegments) {
      const headerOffsetSegment = headerLateralOffset ? offsetPolyLine2(segment, headerLateralOffset) : segment;
      if (includeStructure) {
        linework.push(polyLine2Thicken(headerOffsetSegment, internalHeaderStructureWidth, true));
      }
      linework.push(
        offsetPolyLine2(
          headerOffsetSegment,
          porch.porchSettings.headerCasingThickness / 2 + porch.porchSettings.headerCasingOffsetFromCenterLine,
        ),
      );
      linework.push(
        offsetPolyLine2(
          headerOffsetSegment,
          -(porch.porchSettings.headerCasingThickness / 2 - porch.porchSettings.headerCasingOffsetFromCenterLine),
        ),
      );
    }
  }
}

export function makePorchColumnLinework(porch: Porch, linework: PolyLine2[], includeStructure: boolean = true) {
  const { internalColumnStructureSize, removeColumns } = porch.porchSettings;

  if (!removeColumns) {
    linework.push(...porch.columnProfiles);

    if (includeStructure) {
      linework.push(
        ...porch.colonnadePoints
          .filter((x) => x.type === "COLUMN" || x.type === "MULTI_COLUMN")
          .map((x) => rect2ByCenterWidthHeight(x.x, x.y, internalColumnStructureSize, internalColumnStructureSize)),
      );
    }
  }
}
